import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVehiclesContext } from '../../context/VehiclesContext';
import { Asset, Vehicle } from '../../state/types';

export const useVehicleTableLogic = () => {
  const navigate = useNavigate();
  // Get all shared data and setters from the context
  const { vehicles, selectedVehicles, setSelectedVehicles } = useVehiclesContext();

  // State that is purely for UI interaction within the table/list
  const [openModal, setOpenModal] = useState(false);
  const [modalVehicle, setModalVehicle] = useState<{ clientId: string; assetId: string } | null>(null);
  const [thumbnailErrors, setThumbnailErrors] = useState<Record<string, boolean>>({});

  // Reset local state when the main vehicle list changes
  useEffect(() => {
    setThumbnailErrors({});
  }, [vehicles]);

  const handleRowClick = (clientId: string, assetId: string) => {
    navigate(`/vehicle/${clientId}/${assetId}`);
  };

  const handleThumbnailError = (assetId: string) => {
    setThumbnailErrors((prev) => ({ ...prev, [assetId]: true }));
  };

  const handle360Click = (event: React.MouseEvent, clientId: string, assetId: string) => {
    event.stopPropagation();
    setModalVehicle({ clientId, assetId });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalVehicle(null);
  };

  const handleCheckboxClick = (event: React.MouseEvent, vehicle: Asset) => {
    event.stopPropagation();
    const isSelected = selectedVehicles.some((v) => v.assetId === vehicle.assetId);
    const updatedSelection = isSelected
      ? selectedVehicles.filter((v) => v.assetId !== vehicle.assetId)
      : [...selectedVehicles, vehicle];
    
    // Update the global state in the context
    setSelectedVehicles(updatedSelection);
  };

  const handleSelectAllClick = () => {
    if (selectedVehicles.length === vehicles.length) {
      setSelectedVehicles([]); // Deselect all
    } else {
      setSelectedVehicles(vehicles); // Select all
    }
  };

  // Helper function for checking if a single vehicle is selected
  const isSelected = (vehicle: Vehicle) => {
    return selectedVehicles.some((v) => v.assetId === vehicle.assetId);
  };

  // Return everything our components need
  return {
    // State
    openModal,
    modalVehicle,
    thumbnailErrors,
    // Handlers
    handleRowClick,
    handleThumbnailError,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    handleSelectAllClick,
    // Computed values / helpers
    isSelected,
    isAllSelected: selectedVehicles.length > 0 && selectedVehicles.length === vehicles.length,
    isIndeterminate: selectedVehicles.length > 0 && selectedVehicles.length < vehicles.length,
  };
};